import { useEffect, useRef, useState } from 'react'

import { api } from '~/api/client'
import { act } from '~/beacon'
import type { ArtistHit, Found, GenreTally, GigHit, VenueTally } from '~/api/types'

import s from './Search.module.css'

/**
 * The site's search. One box, in the bar, on every page.
 *
 * It began in the calendar's toolbar, which is where the Python kept it. That was wrong for
 * the same reason the masthead is shared: a reader on the front page looking for an act had
 * to go to the calendar first to find the control that would have told them. Search is not
 * a property of one half of the site.
 *
 * **Three kinds, because those are the ways somebody names what they are after:** the night
 * itself, who is playing, and where it is. Every one has a page, and choosing a result goes
 * to it — a gig to the gig, an act to their page, a room to its page, and a person to their
 * profile once somebody is signed in to have people.
 *
 * **It never filters the calendar.** An earlier version narrowed the calendar to a room or a
 * brand, which made one control mean two things depending on which half of a row you hit,
 * and made a result on the front page do something invisible on a page you were not looking
 * at. Search reaches things. That is all it does.
 *
 * **Enter opens the lot.** The dropdown is the first few of each kind, which is right when
 * you know what you are after and useless when you are browsing — so the key that means
 * "yes, that one" with something highlighted means "show me all of it" with nothing.
 */
export interface SearchProps {
  /** Whether to offer people. A signed-out reader has nobody to look for. */
  people?: boolean
  onGig(id: string): void
  onArtist(id: number): void
  onVenue(slug: string): void
  /** The tag itself, not a slug — `/g/` carries MusicBrainz's own spelling. */
  onGenre(genre: string): void
  onPerson(username: string): void
  /** Enter with nothing highlighted: everything that matches, on a page of its own. */
  onEverything(query: string): void
}

interface Hits {
  q: string
  events: GigHit[]
  artists: ArtistHit[]
  venues: VenueTally[]
  /** Kinds of music. The fourth way somebody names what they are after. */
  genres: GenreTally[]
  people: Found[]
}

/** `/find`'s half of an answer. */
type Things = Omit<Hits, 'q' | 'people'>

/**
 * How long the box waits after the last keystroke before it asks. One value for the
 * masthead and the Friends panel, so the two boxes cannot drift apart. Shorter asks sooner
 * and sends a pair after more of somebody's pauses; the trade is recorded in the plan (D3).
 */
export const SEARCH_DEBOUNCE_MS = 120

const NIGHT = new Intl.DateTimeFormat('en-GB', {
  day: 'numeric',
  month: 'short',
  timeZone: 'Europe/London',
})


/** The things half: `/find`, which is public. A failure reads as finding nothing. */
async function lookThings(query: string, limit?: number): Promise<Things> {
  const found = await api
    .find(query, limit)
    .catch(() => ({ events: [], artists: [], venues: [], festivals: [], genres: [] }))
  return { events: found.events, artists: found.artists, venues: found.venues, genres: found.genres }
}

/**
 * The people half: `/people`, asked only for somebody signed in — a signed-out reader has
 * nobody to look for, and would only collect 401s. A failure reads as nobody.
 */
function lookPeople(query: string, people: boolean): Promise<Found[]> {
  return people ? api.people(query).catch(() => []) : Promise.resolve([])
}

/**
 * Ask for everything at once.
 *
 * Two requests, because `/find` is public and `/people` needs a session. One endpoint
 * answering both would have to be private, which would put acts and rooms behind a login
 * for no reason.
 *
 * Both halves are awaited together here for the results page, whose total would otherwise
 * change under the reader. The dropdown asks the same two halves and draws each as it lands.
 */
export async function look(
  query: string,
  people: boolean,
  limit?: number,
): Promise<Omit<Hits, 'q'>> {
  const [found, folk] = await Promise.all([lookThings(query, limit), lookPeople(query, people)])
  return { ...found, people: folk }
}

export function Search({
  people = false,
  onGig,
  onArtist,
  onVenue,
  onGenre,
  onPerson,
  onEverything,
}: SearchProps) {
  const [query, setQuery] = useState('')
  const [open, setOpen] = useState(false)
  // Each half is `null` until it lands, so the list can tell "nothing yet" from "nothing".
  const [hits, setHits] = useState<{ q: string; things: Things | null; people: Found[] | null } | null>(null)
  const timer = useRef<number | undefined>(undefined)
  const box = useRef<HTMLDivElement>(null)

  const asked = query.trim()

  useEffect(() => {
    window.clearTimeout(timer.current)
    if (!asked) {
      setHits(null)
      return
    }
    // Live until the query moves on. The stamp below keeps a stale answer off the screen;
    // this keeps it out of state: without it, a slow reply for `ez` landing after the reply
    // for `ezra` replaced the right answer with one nothing would draw, and the list went
    // blank until the next keystroke. The same `live` flag `Results.tsx` uses.
    let live = true
    // The keystroke that made this query, which is where a search's wait starts.
    const typed = performance.now()
    // Stamped with the query that fetched them. Without this the list shows the previous
    // query's answers under the current query's text, which reads as the search being
    // wrong rather than as it being one keystroke behind. Each half is stamped on its own,
    // and merged only into an answer for the same query, so a late half of `ez` can neither
    // land on `ezra` nor wipe the half of `ezra` already drawn.
    const land = (half: { things: Things } | { people: Found[] }) => {
      if (!live) return
      setHits(was => ({ ...(was?.q === asked ? was : { q: asked, things: null, people: null }), ...half }))
    }
    timer.current = window.setTimeout(() => {
      // Timed until the things half is drawn, and nothing is drawn before it: shown is that
      // frame too. A query the reader typed past is never settled, so never sent.
      const found = act('search', typed)
      // Both sent in the same tick; neither waits on the other to be drawn.
      void lookThings(asked).then(things => {
        land({ things })
        if (live) found(true, true)
      })
      void lookPeople(asked, people).then(folk => land({ people: folk }))
    }, SEARCH_DEBOUNCE_MS)
    return () => {
      live = false
      window.clearTimeout(timer.current)
    }
  }, [asked, people])

  // Pressing anywhere else shuts it. A dropdown that stays open behind the thing you just
  // clicked is a dropdown you have to dismiss twice.
  useEffect(() => {
    if (!open) return
    const away = (e: PointerEvent) => {
      if (!box.current?.contains(e.target as Node)) setOpen(false)
    }
    document.addEventListener('pointerdown', away)
    return () => document.removeEventListener('pointerdown', away)
  }, [open])

  const shut = () => {
    setOpen(false)
    setQuery('')
  }

  const answer = hits && hits.q === asked ? hits : null
  // What has landed, with a half still out drawn as empty. `null` until either half lands.
  const rows: Omit<Hits, 'q'> | null =
    answer && (answer.things || answer.people)
      ? {
          ...(answer.things ?? { events: [], artists: [], venues: [], genres: [] }),
          people: answer.people ?? [],
        }
      : null
  // "Nothing matches" is a claim about both halves, so it waits for both.
  const both = Boolean(answer?.things && answer.people)
  const found = rows
    ? rows.events.length +
      rows.artists.length +
      rows.venues.length +
      rows.genres.length +
      rows.people.length
    : 0

  return (
    <div className={s.wrap} ref={box}>
      <input
        className={s.box}
        data-finder=""
        type="search"
        value={query}
        placeholder="Search"
        /* Named as well as placeheld. Under 620px the box is an icon and the placeholder is
           transparent, so the only visible label is a drawn glass — and a placeholder was
           never a name in the first place, it is the text a field shows when it is empty. */
        aria-label="Search"
        autoComplete="off"
        onFocus={() => setOpen(true)}
        onChange={e => {
          setQuery(e.target.value)
          setOpen(true)
        }}
        onKeyDown={e => {
          if (e.key === 'Escape') shut()
          if (e.key === 'Enter' && asked) {
            onEverything(asked)
            shut()
          }
        }}
      />

      {open && asked ? (
        <div className={s.list} role="listbox" aria-label="Results">
          {rows === null || (found === 0 && !both) ? (
            <p className={s.none}>Searching…</p>
          ) : found === 0 ? (
            <p className={s.none}>Nothing matches that.</p>
          ) : (
            <>
              {rows.events.length > 0 ? (
                <>
                  <div className={s.group}>Gigs</div>
                  {rows.events.map(g => (
                    <button
                      key={g.id}
                      type="button"
                      onClick={() => {
                        onGig(g.id)
                        shut()
                      }}
                    >
                      <span>{g.title}</span>
                      <span className={s.count}>
                        {NIGHT.format(new Date(g.startsAt))} · {g.venue}
                      </span>
                    </button>
                  ))}
                </>
              ) : null}

              {rows.artists.length > 0 ? (
                <>
                  <div className={s.group}>Acts</div>
                  {rows.artists.map(a => (
                    <button
                      key={a.id}
                      type="button"
                      onClick={() => {
                        onArtist(a.id)
                        shut()
                      }}
                    >
                      <span>
                        {a.is_favourite ? '★ ' : ''}
                        {a.name}
                      </span>
                      <span className={s.count}>
                        {a.appearances} {a.appearances === 1 ? 'date' : 'dates'}
                      </span>
                    </button>
                  ))}
                </>
              ) : null}

              {rows.venues.length > 0 ? (
                <>
                  <div className={s.group}>Venues</div>
                  {rows.venues.map(v => (
                    <button
                      key={v.slug}
                      type="button"
                      onClick={() => {
                        onVenue(v.slug)
                        shut()
                      }}
                    >
                      <span>
                        {v.tracked ? '★ ' : ''}
                        {v.name}
                      </span>
                      <span className={s.count}>{v.count}</span>
                    </button>
                  ))}
                </>
              ) : null}

              {rows.genres.length > 0 ? (
                <>
                  {/* Below the rooms and above the people: a genre is a broader answer than
                      either, and somebody typing a name wants the name first. */}
                  <div className={s.group}>Genres</div>
                  {rows.genres.map(g => (
                    <button
                      key={g.genre}
                      type="button"
                      onClick={() => {
                        onGenre(g.genre)
                        shut()
                      }}
                    >
                      <span>{g.genre}</span>
                      <span className={s.count}>{g.acts}</span>
                    </button>
                  ))}
                </>
              ) : null}

              {rows.people.length > 0 ? (
                <>
                  <div className={s.group}>People</div>
                  {rows.people.map(p => (
                    <button
                      key={p.id}
                      type="button"
                      onClick={() => {
                        onPerson(p.username)
                        shut()
                      }}
                    >
                      <span>{p.name}</span>
                      <span className={s.count}>@{p.username}</span>
                    </button>
                  ))}
                </>
              ) : null}

              {/* The way out of a list that is only ever the first few of each kind. */}
              <button
                type="button"
                className={s.all}
                onClick={() => {
                  onEverything(asked)
                  shut()
                }}
              >
                <span>Everything matching “{asked}”</span>
                <span className={s.count}>↵</span>
              </button>
            </>
          )}
        </div>
      ) : null}
    </div>
  )
}
